import { createRouter, createWebHistory } from "vue-router";
import { useVideoPlayerStore } from "@/stores/videoPlayer";
import { useAuthStore } from "@/stores/auth";
import { useNavigationTree } from "@/stores/navigationTree";
import { api } from "@/lib/api";
import { loginQuery } from "@/lib/domain/authRedirect";
import { restoreScrollPositions } from "./scrollRestoration";
import { watchForStaleBuild } from "./staleBuildWatch";
import { recoverFromStaleChunks } from "./staleChunkRecovery";
import { trackNavigationProgress } from "./navigationProgress";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(_, __, savedPosition) {
    return savedPosition || { top: 0 };
  },
  routes: [
    {
      path: "/login",
      name: "Login",
      component: () => import("../views/Login.vue"),
    },
    {
      path: "/auth/callback",
      name: "SSO Callback",
      component: () => import("../views/AuthCallback.vue"),
    },
    {
      path: "/register",
      name: "Register",
      component: () => import("../views/Register.vue"),
    },
    {
      path: "/password-reset",
      name: "Password Reset",
      component: () => import("../views/PasswordReset.vue"),
    },
    {
      path: "/mcp/authorize",
      name: "MCP Authorize",
      component: () => import("../views/McpAuthorize.vue"),
    },
    {
      path: "/settings",
      name: "Settings",
      component: () => import("../views/Settings/index.vue"),
    },
    {
      path: "/settings/edit",
      name: "Settings Edit",
      component: () => import("../views/Settings/Edit.vue"),
    },
    {
      path: "/",
      name: "Projects",
      component: () => import("../views/Projects/index.vue"),
    },
    {
      path: "/devices",
      name: "Devices",
      component: () => import("../views/Devices/index.vue"),
    },

    // --- Global views -------------------------------------------------------
    // Navigation sits above any single project, so these live at the root and
    // carry their project (when one is chosen) as ?project_id=. They must be
    // declared before /:project_id, which would otherwise swallow them.
    {
      path: "/playground",
      name: "Playground",
      component: () => import("../views/Playground/index.vue"),
    },
    {
      // Keyed by schedule, not cycle: the schedule id is the only identifier the
      // slim list carries, so the list needs no extra request to build its links.
      // The run screen resolves the cycle from the schedule detail.
      path: "/playground/:schedule_id",
      name: "Playground Run",
      component: () => import("../views/Playground/Run.vue"),
    },
    {
      path: "/runs",
      name: "Batches",
      component: () => import("../views/Batches/index.vue"),
    },
    {
      path: "/schedules",
      name: "Schedules List",
      component: () => import("../views/Schedules/index.vue"),
    },
    {
      path: "/analytics",
      name: "Analysis",
      component: () => import("../views/Analysis/index.vue"),
    },
    {
      // Artifacts span every project — the list is assembled across all of them,
      // so unlike Playground or Runs it carries no ?project_id=.
      path: "/artifacts",
      name: "Artifacts",
      component: () => import("../views/Artifacts/index.vue"),
    },

    // --- Redirects from the old per-project paths ---------------------------
    // Old links stay alive; the project moves from the path into the query.
    {
      path: "/:project_id/playground",
      redirect: (to) => ({ name: "Playground", query: { project_id: to.params.project_id as string } }),
    },
    {
      path: "/:project_id/batches",
      redirect: (to) => ({ name: "Batches", query: { project_id: to.params.project_id as string } }),
    },
    {
      path: "/:project_id/analysis",
      redirect: (to) => ({ name: "Analysis", query: { project_id: to.params.project_id as string } }),
    },

    {
      path: "/:project_id",
      name: "Project",
      component: () => import("../views/Projects/:project_id.vue"),
    },
    {
      // A drawer over the project page rather than a page of its own — the same
      // component, which opens the drawer when it finds itself on this route.
      path: "/:project_id/settings",
      name: "Project Settings",
      component: () => import("../views/Projects/:project_id.vue"),
    },
    {
      path: "/:project_id/edit",
      redirect: (to) => ({ name: "Project Settings", params: { project_id: to.params.project_id as string } }),
    },
    {
      path: "/:project_id/:suite_id",
      name: "Suite",
      component: () => import("../views/Suite/index.vue"),
    },
    {
      path: "/:project_id/:suite_id/:case_id",
      name: "Case",
      component: () => import("../views/Case.vue"),
    },
    {
      path: "/:project_id/:suite_id/:case_id/runs",
      redirect: (to) => to.path.replace("/runs", ""),
    },
    {
      path: "/:project_id/:suite_id/:case_id/learns",
      redirect: (to) => to.path.replace("/learns", ""),
    },
    {
      path: "/:project_id/:suite_id/:case_id/:cycle_id",
      name: "Cycle",
      component: () => import("../views/Cycle/index.vue"),
    },
    {
      path: "/:project_id/batches/:batch_id/:cycle_id",
      name: "Cycle From Batch",
      component: () => import("../views/Cycle/index.vue"),
    },
    {
      path: "/:project_id/batches/:batch_id",
      name: "Batch",
      component: () => import("../views/Batches/:batch_id.vue"),
    },
  ],
});

trackNavigationProgress(router);

// Auth guard
router.beforeEach(async (to, from, next) => {
  // The SSO callback establishes the session itself; let it through unauthenticated.
  if (to.name === "SSO Callback") {
    next();
    return;
  }

  const authStore = useAuthStore();
  const isAuthenticated = authStore.isAuthenticated;

  if (["Register", "Password Reset"].includes(String(to.name))) {
    isAuthenticated || api.auth.token ? next({ name: "Projects" }) : next();
    return;
  }

  if (to.name === "Login") {
    isAuthenticated ? next({ name: from.name }) : next();
    return;
  }

  if (isAuthenticated && authStore.profile) {
    next();
    return;
  }

  try {
    await authStore.initAuth();
    next();
  } catch {
    next({ name: "Login", query: loginQuery(to.fullPath) });
  }
});

// Handle video player state during navigation
router.beforeEach((to, from, next) => {
  const videoStore = useVideoPlayerStore();

  // Every route that mounts a VideoPlayer against the shared store — a Playground
  // run screen plays its recording the same way a cycle does.
  const isVideoRoute = (name: string | symbol | undefined) =>
    name === "Cycle" || name === "Cycle From Batch" || name === "Playground Run";

  const entersOrLeavesPlayer = isVideoRoute(to.name) !== isVideoRoute(from.name);
  const switchesRecording =
    isVideoRoute(to.name) &&
    isVideoRoute(from.name) &&
    (to.params.cycle_id !== from.params.cycle_id ||
      to.params.batch_id !== from.params.batch_id ||
      to.params.schedule_id !== from.params.schedule_id);

  if (entersOrLeavesPlayer || switchesRecording) {
    videoStore.reset();
  } else if (to.params.cycle_id || to.params.batch_id) {
    videoStore.setSidePlayerOpen(false);
  }

  next();
});

router.afterEach((to) => {
  useNavigationTree().setRoute(to);
});

// Lists hand back the place you were reading when you come back to them.
restoreScrollPositions(router);

// A tab left open across a deploy holds a module graph the server has already
// deleted: notice that on the way back to the tab, and catch the import that
// slips through anyway.
watchForStaleBuild(router);
recoverFromStaleChunks(router);

export default router;
